import { useEffect, useMemo } from 'react';
import type { MyTransactionsRequest } from 'src/apis/capacity-management/use-my-transactions';
import type {
  DealsSurrenderabilityData,
  DealsSurrenderabilityResponse,
} from 'src/apis/surrender-service/types';
import type { OptionalSurrenderRootRequest } from 'src/apis/surrender-service/use-surrender-root';
import { getSurrenderabilityData } from 'src/apis/surrender-service/utils';
import { useAxios } from 'src/hooks/use-axios';

type ExecuteProps = {
  url: string;
  data: DealsSurrenderabilityData;
};

export function useOptionalDealsSurrenderability(
  myTransactionsRequest: MyTransactionsRequest,
  surrenderRoot: OptionalSurrenderRootRequest
) {
  const request = useAxios(
    (axios, baseConfig, { url, data }: ExecuteProps) =>
      axios.request<DealsSurrenderabilityResponse>({
        ...baseConfig,
        url,
        method: 'POST',
        data,
      }),
    { neededOnPageLoad: false }
  );

  const executeProps = useMemo(() => {
    const getDealsSurrenderability =
      surrenderRoot.response?.data._links.getDealsSurrenderability;
    const items = myTransactionsRequest.response?.data._embedded?.items;

    if (!getDealsSurrenderability || !items) {
      return;
    }

    const data = getSurrenderabilityData(items);

    if (!data) {
      return;
    }

    return { url: getDealsSurrenderability.href, data };
  }, [myTransactionsRequest.response, surrenderRoot.response]);

  /* Refreshing the transactions table gives us a new response object, so the request is keyed on
  its content to not re-fetch the surrenderability for allocations we already asked about. */
  const executeKey = executeProps && JSON.stringify(executeProps);

  useEffect(() => {
    if (!executeProps) {
      return;
    }

    request.execute(executeProps);
    // oxlint-disable-next-line exhaustive-deps
  }, [executeKey]);

  if (!executeProps) return;

  return request;
}

export type OptionalDealsSurrenderabilityRequest = ReturnType<
  typeof useOptionalDealsSurrenderability
>;
